import { useState } from 'react';
import { InvoiceClient, InvoiceDTO, ApiResponse_string } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

export const useInvoiceApi = () => {
    const apiClient = new InvoiceClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: InvoiceDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createInvoice(data);
        } catch (err: any) {
            setError(err.message || 'Error creating Invoice');
        } finally {
            setIsLoading(false);
        }
    };

    const createAndFinalize = async (data: InvoiceDTO) => {
        setIsLoading(true);
        try {
            // Call the new endpoint that creates and immediately finalizes
            const response = await axiosInstance.post(`${API_BASE_URL}/invoice/create-finalized`, data);
            return response.data;
        } catch (err: any) {
            setError(err.message || 'Error creating and finalizing Invoice');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: InvoiceDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateInvoice(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating Invoice');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getInvoiceById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching Invoice');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchInvoices(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching Invoices');
        } finally {
            setIsLoading(false);
        }
    };

    const searchMemberInvoices = async (memberId: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getMemberDisplayInvoices(memberId);
        } catch (err: any) {
            setError(err.message || 'Error searching member Invoices');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteInvoice(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting Invoice');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportInvoices(filters);
        } catch (err: any) {
            setError(err.message || 'Error exporting Invoices');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getInvoiceSasUrl = async (id: string): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getInvoiceNoticeSasUrl(id);
            return response;
        } catch (err: any) {
            console.error('Error getting invoice SAS URL:', err);
            setError(err.response?.data?.message || 'Failed to get invoice SAS URL');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const revert = async (id: string) => {
      
        try {
            const response = await apiClient.revertInvoice(id);
            return response;
        } catch (err: any) {
            console.error('Error reverting invoice:', err);
            setError(err.response?.data?.message || 'Failed to revert invoice');
            throw err;
        } finally {
          
        }
    };

    const resend = async (id: string) => {
        setIsLoading(true);
        try {
            const response = await apiClient.resendInvoiceOrNotice(id);
            return response;
        } catch (err: any) {
            console.error('Error resending invoice or notice:', err);
            setError(err.response?.data?.message || 'Failed to resend invoice or notice');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return { create, createAndFinalize, edit, getById, search, searchMemberInvoices, extract, remove, isLoading, getInvoiceSasUrl, revert, resend, error };
};